import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Loader2, MapPin, Phone, CheckCircle2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { phFormat } from '@/lib/ph'

export const Route = createFileRoute('/t/$token')({ component: PublicTracking })

interface TrackingData {
  state: 'on_the_way' | 'arrived'
  eta_at: string | null
  started_at: string
  company: { name: string | null; logo: string | null; accent_color: string | null; phone: string | null }
  tech: { first_name: string | null }
  job: { address: string | null }
  position: { lat: number; lng: number; recorded_at: string } | null
}

const fmtTime = (iso: string) =>
  phFormat(new Date(iso), 'time', { hour: 'numeric', minute: '2-digit' })

function PublicTracking() {
  const { token } = Route.useParams()

  const q = useQuery({
    queryKey: ['public-tracking', token],
    queryFn: async (): Promise<TrackingData | null> => {
      const { data, error } = await supabase.rpc('public_get_tracking', { p_token: token })
      if (error) throw error
      return data as TrackingData | null
    },
    // The whole point of the page is that it stays current without the customer refreshing.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })

  if (q.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas-subtle">
        <Loader2 className="h-6 w-6 animate-spin text-token-muted" />
      </div>
    )
  }

  // Unknown, ended and expired tokens are deliberately indistinguishable here — the RPC returns
  // null for all three so this page can never confirm whether a given link ever existed.
  if (!q.data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas-subtle px-4 text-center">
        <div>
          <p className="font-display text-[20px] font-bold text-token-primary">This tracking link has ended</p>
          <p className="mt-1 text-[14px] text-token-secondary">Your tradesperson has finished sharing their location.</p>
        </div>
      </div>
    )
  }

  const { state, eta_at, company, tech, job, position } = q.data
  const accent = company.accent_color || '#0038a8'
  const companyName = (company.name || 'Your tradesperson').trim()
  const techName = tech.first_name?.trim()
  const arrived = state === 'arrived'

  // Static map: no API key reaches this page and no script is loaded — a pin on OSM is enough to
  // answer "how far away are they?", which is the only question this page exists to answer.
  const mapSrc = position
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${position.lng - 0.012}%2C${position.lat - 0.008}%2C${position.lng + 0.012}%2C${position.lat + 0.008}&layer=mapnik&marker=${position.lat}%2C${position.lng}`
    : null

  return (
    <div className="min-h-screen bg-canvas-subtle px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-md">
        <div className="mb-5 flex items-center gap-2.5">
          {company.logo
            ? <img src={company.logo} alt={companyName} className="h-9 w-9 rounded-[10px] object-cover" />
            : <span className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[16px] font-bold text-white" style={{ background: accent }}>{companyName[0]?.toUpperCase()}</span>}
          <p className="font-display text-[17px] font-bold tracking-[-0.02em] text-token-primary">{companyName}</p>
        </div>

        <div className="overflow-hidden rounded-[16px] border border-token-subtle bg-token-surface">
          <div className="px-5 py-6 text-center">
            {arrived ? (
              <>
                <CheckCircle2 className="mx-auto h-8 w-8" style={{ color: accent }} />
                <p className="mt-2.5 font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">
                  {techName ? `${techName} has arrived` : 'Your tradesperson has arrived'}
                </p>
              </>
            ) : (
              <>
                <p className="text-[11px] font-black uppercase tracking-[0.14em] text-token-secondary">On the way</p>
                <p className="mt-2 font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">
                  {techName ? `${techName} is on the way` : 'Your tradesperson is on the way'}
                </p>
                {eta_at && (
                  <p className="mt-1.5 text-[15px] text-token-secondary">
                    Estimated arrival <span className="font-semibold text-token-primary">{fmtTime(eta_at)}</span>
                  </p>
                )}
              </>
            )}
          </div>

          {mapSrc && !arrived && (
            <iframe
              title="Live location"
              src={mapSrc}
              className="h-56 w-full border-0 border-t border-token-subtle"
              loading="lazy"
            />
          )}

          {/* No position means no recent ping. Say so plainly rather than showing a stale dot or
              an empty map that implies we know where they are. Deliberately does NOT promise the
              map is coming: some clients share an ETA without sharing location at all, and
              "appearing shortly" would be a promise we cannot keep. */}
          {!mapSrc && !arrived && (
            <div className="border-t border-token-subtle px-5 py-4 text-center text-[13px] text-token-secondary">
              Live location isn’t being shared for this job.
            </div>
          )}

          {job.address && (
            <div className="flex items-start gap-2.5 border-t border-token-subtle px-5 py-4">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-token-secondary" />
              <p className="text-[14px] text-token-primary">{job.address}</p>
            </div>
          )}

          {company.phone && (
            <a
              href={`tel:${company.phone.replace(/[^\d+]/g, '')}`}
              className="flex items-center justify-center gap-2 border-t border-token-subtle px-5 py-4 text-[14px] font-semibold text-token-primary hover:bg-canvas-subtle"
            >
              <Phone className="h-4 w-4" /> Call {companyName}
            </a>
          )}
        </div>

        <p className="mt-4 text-center text-[11.5px] text-token-secondary">
          Location is shared only until this job starts, and never afterwards.
        </p>
      </div>
    </div>
  )
}
